/**
 * File: agedDebtors.ts
 * Path: packages/config/src/invoices/agedDebtors.ts
 * Description: Aged debtors read from the invoice ledger, so Money, the
 * Overview and Reports > Aged debtors show one figure: every open invoice in
 * the bucket of its own days past due, and money not tied to an invoice
 * (overpaid invoices, unallocated lines) in Current. The total always equals
 * the Trade debtors account.
 */

import { type AgedBuckets, type AgedReport, type AgedRow } from "../accounting/reports";
import { normaliseName } from "../contracts/contractImportRows";
import type { InvoiceLedger } from "./invoiceLedger";

const bucketOf = (daysLate: number): number => (daysLate <= 0 ? 0 : daysLate <= 30 ? 1 : daysLate <= 60 ? 2 : daysLate <= 90 ? 3 : 4);

const DAY_MS = 86_400_000;
const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);

export const agedDebtors = (ledger: InvoiceLedger): AgedReport => {
  const rows = new Map<string, AgedRow & { clients: Set<string | null>; oldest: string | null }>();
  const add = (party: string, clientId: string | null, bucket: number, cents: number, openSince: string | null) => {
    const key = clientId ? `id:${clientId}` : `name:${normaliseName(party) || party}`;
    const row =
      rows.get(key) ??
      ({ party: party || "No name given", bucketCents: [0, 0, 0, 0, 0] as AgedBuckets, totalCents: 0, oldestDays: 0, clientId, clients: new Set(), oldest: null });
    row.bucketCents[bucket] += cents;
    row.totalCents += cents;
    row.clients.add(clientId);
    if (openSince && (row.oldest === null || openSince < row.oldest)) row.oldest = openSince;
    rows.set(key, row);
  };
  for (const inv of ledger.invoices) {
    if (inv.journalId === null || inv.status === "void" || inv.balanceCents === 0) continue;
    if (inv.balanceCents > 0) add(inv.party, inv.clientId, bucketOf(inv.daysLate), inv.balanceCents, inv.issuedOn);
    else add(inv.party, inv.clientId, 0, inv.balanceCents, null);
  }
  for (const u of ledger.unallocated) add(u.party, u.clientId, 0, u.cents, u.cents > 0 ? u.date : null);

  const list: AgedRow[] = [...rows.values()]
    .filter((r) => r.totalCents !== 0)
    .map((r) => {
      const [only] = [...r.clients];
      return {
        party: r.party,
        bucketCents: r.bucketCents,
        totalCents: r.totalCents,
        oldestDays: r.oldest ? Math.max(0, daysBetween(r.oldest, ledger.asAt)) : 0,
        clientId: r.clients.size === 1 && only ? only : null,
      };
    })
    .sort((a, b) => b.oldestDays - a.oldestDays || b.totalCents - a.totalCents || a.party.localeCompare(b.party));
  const bucketCents: AgedBuckets = [0, 0, 0, 0, 0];
  for (const row of list) row.bucketCents.forEach((c, i) => (bucketCents[i] += c));
  const totalCents = bucketCents.reduce((s, c) => s + c, 0);
  return {
    side: "debtors",
    asAt: ledger.asAt,
    rows: list,
    bucketCents,
    totalCents,
    overdueCents: totalCents - bucketCents[0],
    oldestDays: list.reduce((m, r) => Math.max(m, r.oldestDays), 0),
  };
};

